/**
 * Export Data Button
 *
 * Icon-only export control for the Bill of Materials header bar. Clicking
 * immediately downloads the estimate as a data file (config metadata + BOM,
 * built by utils/csv.ts) — no dialog. User-facing name is
 * "Export Bill of Material (.CSV)" (tooltip/aria).
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { FileDown } from 'lucide-react';
import { useEstimator } from '../../context/EstimatorContext';
import { downloadEstimateCsv } from '../../utils/csv';
import { trackCsvExported } from '../../utils/gtmTracking';
import { calculationModeLabel, colorLabel, orientationLabel, spaceTypeLabel } from './configDataPoints';
import './ExportDataButton.css';

interface ExportDataButtonProps {
  estimateId?: string;
  /** Pass estimate directly (e.g. from shared estimate page) to bypass context */
  estimate?: import('../../types').EstimateOutput;
  /** Saved/shared estimate name — used for the download filename. */
  estimateName?: string;
}

export const ExportDataButton: React.FC<ExportDataButtonProps> = ({ estimateId, estimate, estimateName }) => {
  const { t } = useTranslation('results');
  const { state } = useEstimator();
  const estimateResult = estimate || state.estimateResult;
  const { wallsConfig, ceilingConfig, applicationConfig, calculationMode } = state;

  const handleExport = () => {
    if (!estimateResult) return;

    trackCsvExported(calculationMode, estimateResult);

    // Metadata comes from the estimate's own input record — the config that
    // actually produced this BOM — not the live wizard context, which is
    // stale on shared-estimate pages and after mode changes.
    const input = estimateResult.input;
    const mode = input?.calculationMode || calculationMode;
    const wallCfg = input?.fullRoomWallConfig;
    const hasWalls = mode !== 'CeilingOnly';
    const includeCeiling = input ? input.includeCeiling : ceilingConfig.include;
    const garage = wallCfg?.garage ?? applicationConfig.garage;
    downloadEstimateCsv(estimateResult, {
      projectTitle: estimateName || estimateResult.projectInfo?.projectName,
      estimateUrl: estimateId ? `${window.location.origin}/estimate/${estimateId}` : undefined,
      projectType: calculationModeLabel(mode),
      spaceType: spaceTypeLabel(applicationConfig.spaceType),
      roomLengthFt: input?.roomDimensions?.length,
      roomWidthFt: input?.roomDimensions?.width,
      wallHeightFt: input?.roomDimensions?.height,
      wallColor: hasWalls ? colorLabel(wallCfg?.wallColor ?? wallsConfig.color) : undefined,
      wallOrientation: hasWalls ? orientationLabel(wallCfg?.wallOrientation ?? wallsConfig.orientation) : undefined,
      ceilingIncluded: includeCeiling,
      ceilingColor: includeCeiling ? colorLabel(input?.ceilingConfig?.color ?? ceilingConfig.color) : undefined,
      garageDoors: garage?.enabled && garage.numDoors > 0 ? garage.numDoors : undefined,
    });
  };

  return (
    <button
      type="button"
      className="bom-icon-btn"
      onClick={handleExport}
      disabled={!estimateResult}
      title={t('export.csv')}
      aria-label={t('export.csv')}
    >
      <FileDown size={18} />
    </button>
  );
};

export default ExportDataButton;
